import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, DatePicker, Input, Modal, Select } from 'antd';
import moment from 'moment';
import { Paperclip, X } from 'lucide-react';

import { useStaffUsers } from 'core/hooks';

import { AuthorAvatar } from '../AuthorAvatar';
import {
  ICreateTaskDraft,
  ITaskSubject,
  PRIORITIES,
  addDays,
  localToday,
} from './taskBuckets';
import { PRIORITY_ICONS, STATUS_ICONS } from './taskIcons';
import { IPickedLink, TaskLinkPicker } from './TaskLinkPicker';
import { MarkdownField } from './MarkdownField';
import * as L from './TaskList.styled';
import * as M from './TaskModal.styled';
import * as S from './TaskBoard.styled';

const STATUSES = ['open', 'in_progress'] as const;

interface ITaskCreateModalProps {
  open: boolean;
  onClose: () => void;
  /** `openAfter` — открыть созданную задачу («Создать и открыть»). */
  onCreate: (
    draft: ICreateTaskDraft,
    openAfter?: boolean,
    files?: File[],
  ) => Promise<boolean>;
  adding: boolean;
  /** Fixed group scope (group tab) — hides the subject picker. */
  groupId?: string;
  /** Fixed student scope (student tab) — hides the subject picker. */
  studentId?: string;
  /** Fixed teacher scope (teacher tab) — hides the subject picker. */
  teacherId?: string;
  /** Default group when none is seeded (e.g. the command center's rail). */
  defaultGroupId?: string;
  /**
   * Имя этой группы. Без него чип предвыбранной связи печатает сам
   * идентификатор: пикер берёт подпись из своей памяти имён, а она после
   * подстановки извне пуста.
   */
  defaultGroupName?: string;
  /** Готовая строка «где создаём» — «Мои задачи», «Группа PY01». */
  context?: string;
  /**
   * Хозяин умеет ОТКРЫТЬ созданную задачу. Палитра команд, например, не
   * умеет: там окно живёт само по себе, доски под ним нет. Кнопка «Создать и
   * открыть», которая создаёт и не открывает, хуже отсутствующей.
   */
  canOpenAfter?: boolean;
}

/**
 * Окно создания задачи (ТЗ 0065).
 *
 * 🔴 Раскрывашки «Дополнительно» здесь БОЛЬШЕ НЕТ. Под ней лежали описание,
 * исполнитель, приоритет и связи — то есть всё, кроме названия и срока; аудит
 * справедливо назвал это «важные поля спрятаны». Быстрый случай («позвонить
 * Ане») закрывает теперь не она, а строка создания прямо в колонке доски: там
 * название + Enter и ни одного лишнего поля. Раз есть быстрый путь, полная
 * форма имеет право быть полной.
 *
 * Контролы — antd, как в окне задачи. До 0065 создание было собрано на голых
 * `input/select/textarea`, а правка — на antd: два окна об одной сущности
 * выглядели как два разных продукта, причём нативный `input type="date"`
 * показывал «dd/mm/yyyy» посреди русского интерфейса.
 */
export const TaskCreateModal = memo(
  ({
    open,
    onClose,
    onCreate,
    adding,
    groupId,
    studentId,
    teacherId,
    defaultGroupId,
    defaultGroupName,
    context,
    canOpenAfter = false,
  }: ITaskCreateModalProps) => {
    const { t } = useTranslation();
    const today = localToday();
    const tomorrow = addDays(today, 1);
    // On a scoped board (group / student / teacher tab) the subject is fixed by
    // the host page, so the picker is hidden entirely.
    const scoped = Boolean(groupId || studentId || teacherId);

    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    // Файлы к ещё не созданной задаче. Уходят вместе с ней одной мутацией.
    const [files, setFiles] = useState<File[]>([]);
    const fileRef = useRef<HTMLInputElement | null>(null);
    // Связи вместо «предмета» (ревизия 0064): в карточке видов двенадцать,
    // а при создании было три — чтобы прицепить курс или оператора,
    // приходилось создать задачу и открыть её заново.
    const [links, setLinks] = useState<IPickedLink[]>([]);
    const [formDue, setFormDue] = useState('');
    const [priority, setPriority] = useState('normal');
    const [status, setStatus] = useState('open');
    const [formAssigneeId, setFormAssigneeId] = useState('');

    /**
     * Сброс формы на каждом открытии.
     *
     * Механизма «seed» здесь больше нет: колонки доски создают задачу СТРОКОЙ
     * НА МЕСТЕ (ТЗ 0065), а не открывают это окно, и после той правки
     * `openCreate` перестал вызываться с аргументом вовсе. Тип
     * `ITaskCreateSeed` при этом продолжал жить и обещать в доках подстановку
     * срока и статуса из колонки — обещание, которого никто не исполнял, и
     * ловушку впридачу: `seed.status = 'done'` не совпал бы ни с одним
     * сегментом, и форма показала бы пустой выбор при непустом статусе.
     */
    useEffect(() => {
      if (!open) return;
      setTitle('');
      setDescription('');
      setFormAssigneeId('');
      setPriority('normal');
      setStatus('open');
      setFormDue('');
      setLinks(
        defaultGroupId
          ? [
              {
                kind: 'group',
                id: defaultGroupId,
                name: defaultGroupName || defaultGroupId,
              },
            ]
          : [],
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const { staffUsers } = useStaffUsers(false);

    // Задача без связей — законное личное дело (ТЗ 0014), так что связи
    // ничего не требуют: единственное обязательное поле — название.
    const canCreate = Boolean(title.trim());

    const submit = useCallback(
      async (openAfter: boolean) => {
        if (!canCreate || adding) return;
        // На скоупленной доске предмет задаёт страница-хозяин.
        let scopedSubject: ITaskSubject = { kind: 'group', id: groupId };
        if (teacherId) scopedSubject = { kind: 'teacher', id: teacherId };
        else if (studentId) scopedSubject = { kind: 'student', id: studentId };
        const ok = await onCreate(
          {
            title: title.trim(),
            description: description.trim() || undefined,
            subject: scoped ? scopedSubject : undefined,
            links: scoped
              ? undefined
              : links.map((row) => ({ kind: row.kind, id: row.id })),
            assigneeId: formAssigneeId || undefined,
            dueDate: formDue || undefined,
            priority,
            status,
          },
          openAfter,
          files,
        );
        if (ok) onClose();
      },
      [
        canCreate,
        adding,
        onCreate,
        title,
        description,
        files,
        scoped,
        links,
        groupId,
        studentId,
        teacherId,
        formAssigneeId,
        formDue,
        priority,
        status,
        onClose,
      ],
    );

    return (
      <Modal
        visible={open}
        width={860}
        style={{ maxWidth: 'calc(100vw - 32px)' }}
        title={t('pages.tasks.createModalTitle')}
        onCancel={onClose}
        destroyOnClose
        footer={
          <M.Footer>
            {context ? <M.FooterNote>{context}</M.FooterNote> : null}
            <Button onClick={onClose}>{t('button.cancel')}</Button>
            {canOpenAfter ? (
              <Button
                disabled={!canCreate}
                loading={adding}
                onClick={() => submit(true)}
              >
                {t('pages.tasks.create.andOpen')}
              </Button>
            ) : null}
            <Button
              type="primary"
              disabled={!canCreate}
              loading={adding}
              onClick={() => submit(false)}
            >
              {t('pages.tasks.add')}
            </Button>
          </M.Footer>
        }
      >
        <M.Section>
          <S.DrawerLabel>
            {t('pages.communications.taskTitleLabel')}
          </S.DrawerLabel>
          <Input
            autoFocus
            value={title}
            maxLength={255}
            placeholder={t('pages.dashboard.taskTitlePlaceholder')}
            onChange={(e) => setTitle(e.target.value)}
            onPressEnter={() => submit(false)}
          />
        </M.Section>

        <M.CreateGrid>
          <div>
            <S.DrawerLabel>{t('pages.tasks.status.label')}</S.DrawerLabel>
            <M.SegGroup $cols={2} role="group">
              {STATUSES.map((key) => {
                const Icon = STATUS_ICONS[key];
                return (
                  <M.Seg
                    key={key}
                    type="button"
                    $active={status === key}
                    aria-pressed={status === key}
                    onClick={() => setStatus(key)}
                  >
                    {Icon ? (
                      <Icon size={12} strokeWidth={2} aria-hidden="true" />
                    ) : null}
                    {t(`pages.tasks.status.${key}`)}
                  </M.Seg>
                );
              })}
            </M.SegGroup>
          </div>
          <div>
            <S.DrawerLabel>
              {t('pages.communications.taskPriorityLabel')}
            </S.DrawerLabel>
            <M.SegGroup $cols={3} role="group">
              {PRIORITIES.map((key) => {
                const Icon = PRIORITY_ICONS[key];
                return (
                  <M.Seg
                    key={key}
                    type="button"
                    $active={priority === key}
                    aria-pressed={priority === key}
                    onClick={() => setPriority(key)}
                  >
                    {Icon ? (
                      <Icon size={12} strokeWidth={2} aria-hidden="true" />
                    ) : null}
                    {t(`pages.tasks.priority.${key}`)}
                  </M.Seg>
                );
              })}
            </M.SegGroup>
          </div>
        </M.CreateGrid>

        <M.CreateGrid>
          <div>
            <S.DrawerLabel>{t('pages.tasks.drawer.due')}</S.DrawerLabel>
            <L.ChipRow>
              <L.FilterChip
                type="button"
                active={formDue === today}
                aria-pressed={formDue === today}
                onClick={() => setFormDue(today)}
              >
                {t('pages.tasks.create.due.today')}
              </L.FilterChip>
              <L.FilterChip
                type="button"
                active={formDue === tomorrow}
                aria-pressed={formDue === tomorrow}
                onClick={() => setFormDue(tomorrow)}
              >
                {t('pages.tasks.create.due.tomorrow')}
              </L.FilterChip>
              <L.FilterChip
                type="button"
                active={!formDue}
                aria-pressed={!formDue}
                onClick={() => setFormDue('')}
              >
                {t('pages.tasks.create.due.none')}
              </L.FilterChip>
              <DatePicker
                value={formDue ? moment(formDue) : null}
                allowClear
                format="DD.MM.YYYY"
                // Подпись обязательна: без неё читалка возьмёт `placeholder`
                // «Без срока» — имя ПРОТИВОПОЛОЖНОГО состояния. У прежнего
                // нативного поля `aria-label` был.
                aria-label={t('pages.tasks.drawer.due')}
                placeholder={t('pages.tasks.drawer.noDue')}
                onChange={(m) => setFormDue(m ? m.format('YYYY-MM-DD') : '')}
              />
            </L.ChipRow>
          </div>
          <div>
            <S.DrawerLabel>{t('pages.tasks.assignee')}</S.DrawerLabel>
            <Select
              showSearch
              style={{ width: '100%' }}
              value={formAssigneeId || ''}
              filterOption={(input, option) =>
                String(option?.title || '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              onChange={(value) => setFormAssigneeId(value)}
            >
              <Select.Option value="" title={t('pages.tasks.assigneeMe')}>
                {t('pages.tasks.assigneeMe')}
              </Select.Option>
              {staffUsers.map((user) => (
                <Select.Option
                  key={user.id}
                  value={user.id}
                  title={user.name || user.email}
                >
                  <M.OptionRow>
                    <AuthorAvatar name={user.name || user.email} size={18} />
                    {user.name || user.email}
                  </M.OptionRow>
                </Select.Option>
              ))}
            </Select>
          </div>
        </M.CreateGrid>

        <M.Section>
          <S.DrawerLabel>
            {t('pages.communications.taskDescriptionLabel')}
          </S.DrawerLabel>
          {/* Тот же компонент, что и в окне задачи: разъехавшиеся кнопки
              означали бы, что набранное здесь выглядит иначе там. */}
          <MarkdownField
            value={description}
            rows={5}
            placeholder={t('pages.tasks.descriptionPlaceholder')}
            onChange={setDescription}
            onPasteFiles={(picked) => setFiles((prev) => [...prev, ...picked])}
          />
        </M.Section>

        <M.Section>
          <S.DrawerLabel>{t('pages.tasks.files.title')}</S.DrawerLabel>
          {/* Файлы уходят ОДНОЙ мутацией вместе с задачей: отказ на файле
              откатывает и её, поэтому «задачи без своих файлов» не бывает. */}
          <M.FileZone
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => {
              event.preventDefault();
              setFiles((prev) => [
                ...prev,
                ...Array.from(event.dataTransfer?.files || []),
              ]);
            }}
          >
            {files.map((file, index) => (
              <M.FileRow key={`${file.name}:${file.size}`}>
                <Paperclip size={13} aria-hidden="true" />
                <M.FileMeta>{file.name}</M.FileMeta>
                <M.FileActions>
                  <Button
                    type="text"
                    size="small"
                    aria-label={t('pages.tasks.files.remove')}
                    icon={<X size={13} aria-hidden="true" />}
                    onClick={() =>
                      setFiles((prev) => prev.filter((_, i) => i !== index))
                    }
                  />
                </M.FileActions>
              </M.FileRow>
            ))}
            <Button
              type="dashed"
              size="small"
              icon={<Paperclip size={13} aria-hidden="true" />}
              onClick={() => {
                const node = fileRef.current;
                if (!node) return;
                node.value = '';
                node.click();
              }}
            >
              {t('pages.tasks.files.add')}
            </Button>
            <M.HiddenFileInput
              ref={fileRef}
              type="file"
              multiple
              onChange={(event) =>
                setFiles((prev) => [
                  ...prev,
                  ...Array.from(event.target.files || []),
                ])
              }
            />
          </M.FileZone>
        </M.Section>

        {!scoped ? (
          <M.Section>
            <S.DrawerLabel>{t('pages.tasks.links.filterLabel')}</S.DrawerLabel>
            <M.AddLinkRow>
              <TaskLinkPicker
                value={links}
                onChange={setLinks}
                disabled={adding}
              />
            </M.AddLinkRow>
          </M.Section>
        ) : null}
      </Modal>
    );
  },
);

TaskCreateModal.displayName = 'TaskCreateModal';
